// /client/src/components/TableauAchatsGroupes.js
import React, { useEffect, useMemo, useState } from "react";
import { getReglements } from "./../../../services/achatsServices";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Détecte un libellé lié à un acompte (fr/en + variantes)
const isAcompteText = (s = "") =>
  /acompte|arrhes|d[ée]p[ôo]t|deposit|advance/i.test(s);

/* Tooltip simple (dark-aware) */
const CustomTooltip = ({ active, payload, label, isDark, suffix }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-sm ${
        isDark
          ? "bg-slate-900 border-slate-700 text-slate-100"
          : "bg-white border-slate-200 text-slate-800"
      }`}
    >
      {label && <div className="text-[11px] opacity-70">{label}</div>}
      <div className="text-sm font-semibold">
        {typeof val === "number" ? val.toLocaleString("fr-FR") : String(val)}{" "}
        {suffix || ""}
      </div>
    </div>
  );
};

const CURRENCY = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

const PIE_COLORS = [
  "#6366F1",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#0EA5E9",
  "#A855F7",
  "#14B8A6",
  "#E11D48",
  "#84CC16",
  "#F97316",
  "#06B6D4",
  "#F43F5E",
];

export default function TableauAchatsGroupes({ selectedSeason }) {
  const [rows, setRows] = useState([]);
  const [prixGlobal, setPrixGlobal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!selectedSeason) return;
      setLoading(true);
      try {
        const achats = await getReglements();

        if (!Array.isArray(achats)) return;

        // bornes de la saison (01 août -> 31 juillet)
        const [startYear, endYear] = String(selectedSeason).split("-");
        const startDate = new Date(`${startYear}-08-01T00:00:00`);
        const endDate = new Date(`${endYear}-07-31T23:59:59`);

        // Boutique seulement: Article non nul + saison + EXCLUSION des acomptes
        const filtered = achats.filter((a) => {
          if (!a?.Article) return false;

          const d = new Date(a.date_achat);
          if (!(d >= startDate && d <= endDate)) return false;

          const name = a.Article?.nom || "";
          const desc = a.Article?.description || "";
          const msg = a.message_achat || "";

          // Exclure si l'un des champs indique un acompte
          if (
            isAcompteText(name) ||
            isAcompteText(desc) ||
            /acompte/i.test(msg)
          ) {
            return false;
          }
          return true;
        });

        // Agrégation par article
        const map = new Map();
        for (const a of filtered) {
          const key = a.Article?.nom || "Article";
          if (!map.has(key)) {
            map.set(key, {
              article: key,
              description: a.Article?.description || "",
              quantite: 0,
              prix_total: 0,
            });
          }
          const curr = map.get(key);
          curr.quantite += Number(a.quantite || 0);
          curr.prix_total += Number.parseFloat(a.prix_total || 0);
        }

        const arr = [...map.values()].sort(
          (a, b) => b.prix_total - a.prix_total
        );
        const total = arr.reduce((s, x) => s + x.prix_total, 0);

        if (!cancelled) {
          setRows(arr);
          setPrixGlobal(total);
        }
      } catch (e) {
        console.error("Erreur lors de la récupération des achats", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedSeason]);

  // Données graphiques
  const dataQuantites = useMemo(
    () =>
      rows.map((r) => ({
        article: r.article,
        quantite: r.quantite,
      })),
    [rows]
  );

  const dataChiffre = useMemo(() => {
    const total = prixGlobal || 1;
    return rows.map((r) => ({
      article: r.article,
      part: (r.prix_total * 100) / total,
      prix: r.prix_total,
    }));
  }, [rows, prixGlobal]);

  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark
    ? "rgba(148,163,184,0.12)"
    : "rgba(148,163,184,0.15)";
  const topColor = isDark ? "#9CA3FF" : "#A5B4FC";
  const bottomColor = isDark ? "#6C72D9" : "#818CF8";

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête sobre + accent */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Achats articles boutique (saison {selectedSeason})
        </h3>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Résumé / KPIs */}
      <div className="px-5 sm:px-6 mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Articles distincts
          </div>
          <div className="text-lg font-semibold text-slate-900 dark:text-white">
            {rows.length}
          </div>
        </div>
        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Quantités totales
          </div>
          <div className="text-lg font-semibold text-slate-900 dark:text-white">
            {rows.reduce((s, x) => s + x.quantite, 0).toLocaleString("fr-FR")}
          </div>
        </div>
        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Chiffre d’affaires
          </div>
          <div className="text-lg font-semibold text-slate-900 dark:text-white">
            {CURRENCY.format(prixGlobal || 0)}
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="px-5 pb-5 sm:px-6 sm:pb-6 mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Bar chart quantités */}
        <div className="w-full h-[320px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-3">
          <div className="text-sm font-semibold mb-2">
            Top articles par quantités
          </div>
          {loading ? (
            <div className="animate-pulse h-full rounded-xl bg-slate-200/50 dark:bg-slate-800/50" />
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-600 dark:text-slate-300 p-4">
              Aucune donnée.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="90%">
              <BarChart
                data={dataQuantites}
                margin={{ top: 10, right: 16, left: 0, bottom: 40 }}
              >
                <defs>
                  <linearGradient id="barFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={topColor} stopOpacity={0.95} />
                    <stop
                      offset="100%"
                      stopColor={bottomColor}
                      stopOpacity={0.85}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis
                  dataKey="article"
                  height={40}
                  interval={0}
                  angle={-25}
                  dy={20}
                  tick={{ fill: tick, fontSize: 12 }}
                />
                <YAxis tick={{ fill: tick, fontSize: 12 }} width={36} />
                <Tooltip
                  cursor={{ fill: cursorFill }}
                  content={<CustomTooltip isDark={isDark} suffix="pcs" />}
                />
                <Bar
                  dataKey="quantite"
                  fill="url(#barFill)"
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList
                    dataKey="quantite"
                    position="top"
                    formatter={(v) => v.toLocaleString("fr-FR")}
                    style={{
                      fill: isDark ? "#e2e8f0" : "#0f172a",
                      fontSize: 12,
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Donut CA */}
        <div className="w-full h-[320px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-3">
          <div className="text-sm font-semibold mb-2">
            Répartition du CA par article
          </div>
          {loading ? (
            <div className="animate-pulse h-full rounded-xl bg-slate-200/50 dark:bg-slate-800/50" />
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-600 dark:text-slate-300 p-4">
              Aucune donnée.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Tooltip
                  cursor={{ fill: cursorFill }}
                  content={<CustomTooltip isDark={isDark} />}
                />
                <Pie
                  data={dataChiffre}
                  dataKey="part"
                  nameKey="article"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                  label={({ name, part }) =>
                    part >= 8 ? `${name} ${part.toFixed(0)}%` : ""
                  }
                  labelStyle={{ fill: tick, fontSize: 11 }}
                  stroke={isDark ? "#0f172a" : "#ffffff"}
                  strokeWidth={2}
                  isAnimationActive
                >
                  {dataChiffre.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tableau détaillé */}
      <div className="px-5 pb-6 sm:px-6">
        {/* petit hack pour permettre un scroll bord à bord en mobile */}
        <div className="-mx-5 sm:mx-0">
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
            <table className="min-w-[720px] w-full">
              <thead className="bg-slate-50/90 dark:bg-slate-900/90 uppercase tracking-wide text-xs">
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                    Article
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-white">
                    Quantité
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-white">
                    CA
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                {loading ? (
                  [...Array(4)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3" colSpan={4}>
                        <div className="h-5 bg-slate-200/60 dark:bg-slate-800/60 rounded" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-6 text-center text-slate-600 dark:text-slate-300"
                      colSpan={4}
                    >
                      Aucune vente d’article boutique pour cette saison.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr
                      key={r.article}
                      className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                    >
                      <td className="sticky left-0 z-10 bg-white/90 dark:bg-slate-900/40 backdrop-blur px-4 py-3 text-slate-900 dark:text-slate-100 font-medium">
                        {r.article}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        {r.description || "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-900 dark:text-slate-100">
                        {r.quantite.toLocaleString("fr-FR")}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-900 dark:text-slate-100">
                        {CURRENCY.format(r.prix_total)}
                      </td>
                    </tr>
                  ))
                )}
                {!loading && rows.length > 0 && (
                  <tr className="bg-slate-100/80 dark:bg-slate-800/60">
                    <td className="sticky left-0 z-10 bg-slate-100/80 dark:bg-slate-800/60 px-4 py-3 font-semibold">
                      Total
                    </td>
                    <td className="px-4 py-3 font-semibold" colSpan={2} />
                    <td className="px-4 py-3 text-right font-semibold">
                      {CURRENCY.format(prixGlobal)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
